import { httpLink, splitLink } from '@trpc/client';
import superjson from 'superjson';

import { BackendHttpError } from './backend-http-error.js';

import type { Auth0Client } from '@auth0/auth0-spa-js';
import type { AppRouter } from '@cadstrom/backend/trpc';
import type { TRPCLink } from '@trpc/client';
import type { PostHog } from 'posthog-js';

/** Routers whose procedures need no token. Typed against the backend router, so a rename breaks the build. */
const PUBLIC_ROUTERS = ['emailVerification'] as const satisfies readonly (keyof AppRouter['_def']['record'])[];

/**
 * Whether a tRPC procedure is reachable without a token.
 *
 * @param path - The dotted procedure path, e.g. `emailVerification.resend`.
 * @returns True when the procedure belongs to a public router.
 */
export function isPublicTrpcPath(path: string): boolean {
	const [router] = path.split('.');
	return PUBLIC_ROUTERS.some((name) => name === router);
}

/**
 * Builds the tRPC client's links. Public routers are called without a token; every other call
 * fetches one first and fails if the visitor is unauthenticated. Every call, query or mutation,
 * goes out as a POST, so an input of any size travels in the body.
 *
 * @param auth0Client - Source of the access token.
 * @param posthog - Source of the session id forwarded for log correlation.
 * @returns The links to hand to `createTRPCClient`.
 */
export function createTrpcLinks(
	auth0Client: Pick<Auth0Client, 'getTokenSilently'>,
	posthog: Pick<PostHog, 'get_session_id'>,
): TRPCLink<AppRouter>[] {
	// Lets backend logs be correlated to a session replay.
	const getSessionHeaders = (): Record<string, string> => {
		const posthogSessionId = posthog.get_session_id();
		return posthogSessionId ? { 'x-posthog-session-id': posthogSessionId } : {};
	};

	const createLink = (headers: () => Promise<Record<string, string>> | Record<string, string>) =>
		httpLink<AppRouter>({
			url: '/trpc',
			transformer: superjson,
			// A query whose input is a payload rather than an id (an adjudication preview carries the
			// whole review snapshot) overflows the request-line limits nginx (8 KB) and the load
			// balancer (16 KB) enforce on a GET URL.
			methodOverride: 'POST',
			headers,
			fetch: async (url, options) => {
				const response = await globalThis.fetch(url as RequestInfo, options);
				const contentType = response.headers.get('content-type') ?? '';
				// NestJS returns an HTML error page on unhandled 5xx — intercept before tRPC tries to parse it as JSON.
				if (!response.ok && !contentType.includes('application/json')) {
					throw new BackendHttpError(response.status, url instanceof Request ? url.url : String(url));
				}
				return response;
			},
		});

	return [
		splitLink({
			condition: (operation) => isPublicTrpcPath(operation.path),
			true: createLink(getSessionHeaders),
			false: createLink(async () => {
				// Yes, we are meant to call `getTokenSilently` on every request, as Auth0 manages the access token lifecycle
				// for us. It fetches tokens from the cache if they are not expired, and will only hit the
				// /oauth/token endpoint for a new token if the current one is expired.
				// IMPORTANT: This will throw if the user is not authenticated, which is expected behaviour.
				const token = await auth0Client.getTokenSilently();

				return { Authorization: `Bearer ${token}`, ...getSessionHeaders() };
			}),
		}),
	];
}
